import { useEffect, useState, useCallback } from "react";
import { Link, Navigate, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { Globe2, Church, Sparkles, KeyRound } from "lucide-react";
import PostCard from "@/components/social/PostCard";
import Composer from "@/components/social/Composer";

function JoinBox({ onJoined }) {
  const [code, setCode] = useState("");
  const join = async () => {
    const c = code.trim().split("/").pop();
    if (!c) return;
    try { const r = await api.post(`/social/join/${c}`); toast.success(`Joined ${r.data.org_name}.`); onJoined(); }
    catch (err) { toast.error(err?.response?.data?.detail || "That code didn't work."); }
  };
  return (
    <div className="rounded-xl p-6" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} data-testid="social-join-box">
      <div className="flex items-center gap-2 font-serif-display text-xl" style={{ color: "var(--ink)" }}><KeyRound size={18} style={{ color: "var(--gold)" }} /> Join your church</div>
      <p className="text-sm mt-2" style={{ color: "var(--ink-2)" }}>Paste the invite link or code your church gave you. You can also scan your church's QR code.</p>
      <div className="mt-4 flex gap-2">
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Invite code or link" className="flex-1 min-w-0 px-3 py-2 text-sm" data-testid="social-join-input" />
        <button onClick={join} className="btn-primary" data-testid="social-join-btn">Join</button>
      </div>
    </div>
  );
}

function SupportCard() {
  return (
    <div className="rounded-xl p-5 flex items-start gap-3 flex-wrap" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} data-testid="social-support">
      <Sparkles size={18} style={{ color: "var(--gold)" }} className="mt-0.5 shrink-0" />
      <div className="flex-1 min-w-[12rem]">
        <div className="font-medium text-sm" style={{ color: "var(--ink)" }}>thedisciple.social is free for everyone.</div>
        <p className="text-sm mt-1" style={{ color: "var(--ink-2)" }}>If it blesses you, you can help keep it running with an optional $4.99/mo membership.</p>
      </div>
      <Link to="/membership" className="btn-ghost shrink-0" style={{ padding: "0.5rem 1rem" }} data-testid="social-support-btn">Support the platform</Link>
    </div>
  );
}

function Tabs({ me, tab, setTab }) {
  const item = (key, label, Icon) => (
    <button key={key} onClick={() => setTab(key)} className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-full transition-colors whitespace-nowrap" style={{ background: tab === key ? "var(--ink)" : "transparent", color: tab === key ? "var(--bg-base)" : "var(--ink-2)", border: "1px solid var(--border-strong)" }} data-testid={`social-tab-${key}`}>
      <Icon size={14} /> {label}
    </button>
  );
  return (
    <div className="flex gap-2 overflow-x-auto pb-1" data-testid="social-tabs">
      {me.orgs.map((o) => item(o.id, o.name, Church))}
      {item("network", "Network", Globe2)}
    </div>
  );
}

export default function Social() {
  const { user, loading } = useAuth();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState(null);
  const [posts, setPosts] = useState([]);
  const [more, setMore] = useState(false);
  const [params, setParams] = useSearchParams();
  const focusId = params.get("post");
  const [focus, setFocus] = useState(null);
  useEffect(() => {
    if (!focusId || !user) { setFocus(null); return; }
    api.get(`/social/posts/${focusId}`).then((r) => setFocus(r.data)).catch(() => setFocus(null));
  }, [focusId, user]);

  const loadMe = useCallback(() => api.get("/social/me").then((r) => { setMe(r.data); setTab((t) => t || r.data.orgs[0]?.id || null); }), []);
  useEffect(() => { if (user) loadMe(); }, [user, loadMe]);

  const load = useCallback(async (before) => {
    if (!tab) return;
    const params = tab === "network" ? { scope: "network" } : { scope: "church", org_id: tab };
    if (before) params.before = before;
    const r = await api.get("/social/feed", { params });
    setPosts((p) => (before ? [...p, ...r.data.posts] : r.data.posts));
    setMore(r.data.posts.length === 20);
  }, [tab]);
  useEffect(() => { load().catch(() => toast.error("Couldn't load the feed.")); }, [load]);

  if (loading) return null;
  if (!user) return <Navigate to="/login?next=/social" replace />;
  if (!me) return <p className="max-w-2xl mx-auto px-6 py-16" style={{ color: "var(--muted)" }}>Loading…</p>;

  const update = (np) => setPosts((p) => p.map((x) => (x.id === np.id ? np : x)));
  const remove = (id) => setPosts((p) => p.filter((x) => x.id !== id));
  const composeOrg = tab === "network" ? me.orgs[0]?.id : tab;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10" data-testid="social-page">
      <div className="text-[0.6rem] uppercase tracking-[0.24em]" style={{ color: "#A78BFA" }}>thedisciple.social</div>
      <h1 className="font-serif-display text-4xl sm:text-5xl tracking-[-0.03em] mt-2" style={{ color: "var(--ink)" }}>Your church, together.</h1>
      <div className="mt-8 space-y-4">
        {me.orgs.length === 0 ? <JoinBox onJoined={loadMe} /> : (
          <>
            {focus && (
              <div className="space-y-2" data-testid="social-focus-post">
                <div className="flex items-center justify-between text-xs" style={{ color: "var(--muted)" }}>
                  <span>From your notifications</span>
                  <button onClick={() => setParams({})} className="underline" data-testid="social-focus-close">Close</button>
                </div>
                <PostCard post={focus} canContribute={me.can_contribute} onChange={setFocus} onRemove={() => setParams({})} />
              </div>
            )}
            <Tabs me={me} tab={tab} setTab={setTab} />
            {composeOrg && <Composer orgId={composeOrg} onPosted={(p) => setPosts((x) => [p, ...x])} />}
            {posts.length === 0 && <p className="text-sm text-center py-10" style={{ color: "var(--muted)" }} data-testid="social-empty">No posts yet. Be the first to share something.</p>}
            {posts.map((p) => <PostCard key={p.id} post={p} canContribute={me.can_contribute} onChange={update} onRemove={remove} />)}
            {more && <button onClick={() => load(posts[posts.length - 1].created_at)} className="btn-ghost w-full" data-testid="social-load-more">Load more</button>}
            <SupportCard />
            <details className="pt-6"><summary className="text-xs cursor-pointer" style={{ color: "var(--muted)" }}>Join another church</summary><div className="mt-3"><JoinBox onJoined={loadMe} /></div></details>
          </>
        )}
      </div>
    </div>
  );
}

export function SocialJoin() {
  const { code } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/social/join/${code}`).then((r) => setOrg(r.data.org_name)).catch(() => setErr("That invite link isn't valid.")); }, [code]);
  const join = async () => {
    try { await api.post(`/social/join/${code}`); toast.success(`Welcome to ${org}!`); navigate("/social"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't join."); }
  };
  if (loading) return null;
  return (
    <div className="max-w-md mx-auto px-6 py-20 text-center" data-testid="social-join-page">
      <div className="text-[0.6rem] uppercase tracking-[0.24em]" style={{ color: "#A78BFA" }}>thedisciple.social</div>
      {err ? <p className="mt-6" style={{ color: "#F87171" }}>{err}</p> : (
        <>
          <h1 className="font-serif-display text-4xl mt-3" style={{ color: "var(--ink)" }}>Join {org || "your church"}</h1>
          <p className="text-sm mt-3" style={{ color: "var(--ink-2)" }}>See what's happening, react, and stay connected with your church family. It's free to join.</p>
          {user ? (
            <button onClick={join} className="btn-primary mt-8" data-testid="social-join-confirm">Join {org}</button>
          ) : (
            <div className="mt-8 flex gap-3 justify-center">
              <Link to={`/register?next=/social/join/${code}`} className="btn-primary" data-testid="social-join-register">Create a free account</Link>
              <Link to={`/login?next=/social/join/${code}`} className="btn-ghost" data-testid="social-join-login">Sign in</Link>
            </div>
          )}
        </>
      )}
    </div>
  );
}
